/**
 * @file LegalDocumentRow.tsx
 * @description Карточка документа в списке «Юридические документы»
 * (phase19.16, SB139–SB141; uiux-legal-docs §2, uiux-legal-steps-b-c §6.4):
 * значок вида, название, вид (размер файла, число документов группы),
 * пилюля состояния, адрес на сайте, дата и автор действующей редакции,
 * группа, предупреждения. Вся карточка — ссылка на карточку документа.
 */

import type { LegalDocumentListItemDto } from "@chaiberi/shared";
import { AlertTriangle } from "lucide-react";
import { Link } from "react-router";

import { StatusPill } from "../ui/StatusPill";
import { formatLegalDate, LEGAL_KIND_META, legalKindLine, legalStatusMeta, legalWarningText } from "../../lib/legal/legal-doc-view";

interface Props {
  item: LegalDocumentListItemDto;
  /** Адрес карточки документа. */
  to: string;
}

/** «/offer · 12.10.2026 · Евгений» — адрес и действующая редакция. */
function metaLine(item: LegalDocumentListItemDto): string {
  const parts = [item.href];
  if (item.live) parts.push(formatLegalDate(item.live.publishedAt));
  if (item.live?.publishedByName) parts.push(item.live.publishedByName);
  return parts.join(" · ");
}

export function LegalDocumentRow({ item, to }: Props) {
  const Icon = LEGAL_KIND_META[item.kind].icon;
  const status = legalStatusMeta({ ...item, visibleMembers: item.members?.visible });
  return (
    <Link
      to={to}
      className="flex min-h-14 min-w-0 items-start gap-3 rounded-xl bg-surface-lowest p-3 outline-none transition-colors [box-shadow:inset_0_0_0_1px_var(--border)] hover:bg-surface-low focus-visible:ring-2 focus-visible:ring-ring"
    >
      <Icon aria-hidden className="mt-0.5 size-5 shrink-0 text-on-surface-variant" />
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="type-body min-w-0 break-words hyphens-auto font-medium text-on-surface">{item.title}</span>
          <StatusPill meta={status} />
        </span>
        <span className="type-caption text-on-surface-variant">{legalKindLine(item)}</span>
        <span className="type-caption break-words text-on-surface-variant">{metaLine(item)}</span>
        {item.group ? <span className="type-caption text-on-surface-variant">в группе «{item.group.title}»</span> : null}
        {item.warnings.map((warning) => (
          <span key={warning.code} className="type-caption flex items-start gap-1 text-warning">
            <AlertTriangle aria-hidden className="mt-0.5 size-3.5 shrink-0" />
            {legalWarningText(warning)}
          </span>
        ))}
      </span>
    </Link>
  );
}
